import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ImagePlus, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const FIELDS = [
  { key: "business_name", label: "Business name *", ph: "Golden Hour Bakery" },
  { key: "website", label: "Website (if any)", ph: "https://…" },
  { key: "handles", label: "Social handles", ph: "@instagram · @tiktok · facebook.com/…" },
  { key: "colors", label: "Brand colors", ph: "#FAF6EF cream, #A8785A mocha…" },
  { key: "fonts", label: "Fonts you use", ph: "Playfair Display + Jost" },
  { key: "assets_link", label: "Link to logos / photos", ph: "Google Drive, Dropbox or Canva link" },
  { key: "voice", label: "Brand voice & tone", ph: "Warm, confident, a little playful", long: true },
  { key: "audience", label: "Who are we talking to?", ph: "Busy professionals 28–45 in Austin who…", long: true },
  { key: "goals", label: "Goals for the next 90 days", ph: "Grow to 5k followers, book 20 consults/month…", long: true },
  { key: "competitors", label: "Brands you admire or compete with", ph: "@brand1, @brand2 — what you like about them", long: true },
  { key: "avoid", label: "Anything to avoid", ph: "No stock photos of handshakes, never use emojis…", long: true },
  { key: "notes", label: "Anything else", ph: "Launch dates, promos, seasonal moments…", long: true },
];

export default function OnboardingPage() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(Object.fromEntries(FIELDS.map((f) => [f.key, ""])));
  const [logoName, setLogoName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/account").then(({ data }) => {
      if (data.onboarding) { setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(data.onboarding).filter(([k]) => k in f)) })); setDone(true); }
    }).catch(() => {});
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/account/onboarding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoName(data.logo.filename);
      toast.success("Logo received.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account/onboarding", form);
      toast.success("Thank you — we have everything we need to start.");
      setDone(true);
      setTimeout(() => navigate("/account"), 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return <div className="min-h-screen bg-[#FAF6EF]" />;
  if (!user) {
    return (
      <main className="min-h-screen vector-grid flex items-center justify-center px-6 pt-16" data-testid="onboarding-gate">
        <div className="glass rounded-2xl max-w-md w-full p-10 text-center">
          <h1 className="f-display text-3xl mb-3">Log in to continue</h1>
          <p className="text-sm text-[#8C7562] mb-6">The brand-assets form is tied to your account.</p>
          <Link to="/account" className="btn-primary justify-center w-full">Go to account</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen vector-grid pt-28 pb-24" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <span className="eyebrow">Brand assets · 5 minutes</span>
          <h1 className="f-display text-3xl sm:text-4xl mt-3">Let's start your first month.</h1>
          <p className="text-sm text-[#8C7562] mt-3 leading-relaxed max-w-xl">
            Share the essentials below and we'll begin planning right away. You can come back and update anything later from your account.
          </p>
          {done && <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#A8785A]" data-testid="onboarding-done-note"><Check size={13} /> Already submitted — edits will be sent to us again.</p>}
        </motion.div>
        <form onSubmit={submit} className="glass rounded-2xl p-7 sm:p-10 grid sm:grid-cols-2 gap-5" data-testid="onboarding-form">
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Logo file</label>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="onboarding-logo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full rounded-xl border-2 border-dashed border-[#E8DCCB] bg-[#FFFDF7] hover:border-[#D4A0B4] hover:bg-[#FFF3F8] transition-colors p-5 flex items-center justify-center gap-3 text-sm" data-testid="onboarding-logo-btn">
              <ImagePlus size={16} className="text-[#A8785A]" /> {logoName ? `Received: ${logoName}` : "Upload your logo (PNG, JPG, SVG or WEBP)"}
            </button>
          </div>
          {FIELDS.map((f) => (
            <div key={f.key} className={f.long ? "sm:col-span-2" : ""}>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">{f.label}</label>
              {f.long
                ? <textarea className="field !min-h-[84px]" placeholder={f.ph} value={form[f.key]} onChange={set(f.key)} data-testid={`onboarding-${f.key}`} />
                : <input className="field" placeholder={f.ph} value={form[f.key]} onChange={set(f.key)} required={f.key === "business_name"} data-testid={`onboarding-${f.key}`} />}
            </div>
          ))}
          <button type="submit" disabled={busy} className="btn-primary sm:col-span-2 justify-center" data-testid="onboarding-submit-btn">
            {busy ? "Sending…" : done ? "Update my brand assets" : "Send my brand assets"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </main>
  );
}
